// TableLayout.jsx
import React, { useState, useEffect } from "react";
import autoTable from "jspdf-autotable";
import DownloadLogo from "../assets/images/download.svg";
import Watermark from "../assets/images/Watermark.png";
import Logo from "../assets/images/Logo.png";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import useLocale from "../../src/locales/index";
import html2pdf from "html2pdf.js";

const TableLayout = ({
  showTable = true,
  title,
  data = [],
  columns = [],
  analyticsEventName = "download_report",
  reportTitle = "Overall Investment Report",
  fileName = "investment_report",
  showDisclaimer = true,
  itemsPerPage = 26,
}) => {
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { textData } = useLocale();

  if (!showTable) return null;

  // Function to chunk data into pages
  const chunkData = (array, size) => {
    const chunked = [];
    for (let i = 0; i < array.length; i += size) {
      chunked.push(array.slice(i, i + size));
    }
    return chunked;
  };

  const generatePDF = () => {
    // Chunk data into pages
    const dataChunks = chunkData(data, itemsPerPage);
    const totalPages = dataChunks.length;

    // Create a container for all pages
    const allContent = document.createElement("div");

    dataChunks.forEach((pageData, pageIndex) => {
      const pageNumber = pageIndex + 1;

      // Create content for each page
      const pageContent = `
<section style="position: relative; width: 100%; height: 287mm; flex: 1; margin-top: 0px; padding-bottom: 10px; page-break-after: ${
        pageIndex < totalPages - 1 ? "always" : "auto"
      }; display: flex; flex-direction: column; background-color: #ffffff;">
          
          <!-- Watermark - Now behind content -->
          <img src="${Watermark}" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 1; width: 400px; height: auto; z-index: 99;" />
          
          <!-- Content Container - Now above watermark -->
          <div style="position: relative; z-index: 2; display: flex; flex-direction: column; height: 100% background-color: transparent;">
            
            <!-- Header -->
             ${
               pageIndex === 0
                 ? `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
                <img src="${Logo}" alt="logo" style="width: 60px; height: auto;" />
                <h2 style="text-align: center; font-size: 20px; font-weight: 500; margin: 0; color: #000; font-family: 'Noto Sans Devanagari', sans-serif;">${reportTitle}</h2>
                <div style="width: 60px;"></div> <!-- Spacer for alignment -->
              </div>`
                 : `<div style="height: 30px;"></div>`
             }
              
              <!-- Table Container -->
              <div style="margin-top: 10px;">
                <div style="overflow-x: auto; background-color: #ffffff;">
                  <table style="width: 100%; border-collapse: collapse; background-color: #ffffff;">
                    <thead>
                      <tr style="font-size: 13px; background-color: white; ">
                        ${columns
                          .map(
                            (column) =>
                              `<th style="padding: 4px 4px 6px 4px; text-align: start; vertical-align: middle; border: 1px solid #d1d5db; font-size: 13px; font-weight: 600; color: #000; background-color: white;">${column.label}</th>`
                          )
                          .join("")}
                      </tr>
                    </thead>
                    <tbody>
                      ${pageData
                        .map(
                          (item, index) => `
                        <tr style="background-color: ${
                          index % 2 === 0 ? "#f9f9f9" : "#ffffff"
                        };">
                          ${columns
                            .map((column) => {
                              const value = item[column.key];
                              const displayValue =
                                value === 0 || value === ""
                                  ? "-"
                                  : column.format(value);
                              return `<td style="padding:  6px; border: 1px solid #d1d5db; text-align: start; vertical-align: middle; font-size: 13px; color: #000; background-color: inherit;">${displayValue}</td>`;
                            })
                            .join("")}
                        </tr>
                      `
                        )
                        .join("")}
                    </tbody>
                  </table>
                </div>
              </div>
          
            
            ${
              pageIndex === totalPages - 1
                ? `
              <!-- Disclaimer Section -->
              <div style="margin-top: 30px; background-color: #ffffff; padding: 10px 0;">
                <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 10px; text-align: start; margin-top: 0px; color: #000; padding: 0;">${textData.netWorthContent.descaimber}:</h3>
                <p style="font-size: 12px; text-align: justify; color: #000; line-height: 1.4; margin: 0;">${textData.footerdescription}</p>
              </div>
            `
                : ""
            }
          </div>
          
          <!-- Spacer to push footer to bottom -->
          <div style="flex-grow: 1;"></div>

          <!-- Footer - Always at bottom -->
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #6b7280; padding-top: 20px; margin-top: auto; background-color: #ffffff; z-index: 2; position: relative;">
            <span style="font-weight: 700; color: #000; font-family: 'Times New Roman', Times, serif; font-size: 13px;">Page ${pageNumber}</span>
            <a href="https://growlifefund.com" style="text-decoration: underline; color: #000; font-size: 13px; font-weight: 700; line-height: 1.5; font-family: 'Times New Roman', Times, serif;">www.growlifefund.com</a>
          </div>
        </section>
      `;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = pageContent;
      allContent.appendChild(tempDiv.firstElementChild);
    });

    // Add the content to the document body temporarily
    document.body.appendChild(allContent);

    // Configure html2pdf options
    const options = {
      margin: [3, 10, 3, 10],
      filename: `${fileName}_${new Date()
        .toLocaleDateString()
        .replace(/\//g, "-")}.pdf`,
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
      },
      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
      },
    };

    // Generate and save PDF
    html2pdf()
      .from(allContent)
      .set(options)
      .save()
      .then(() => {
        // Clean up
        document.body.removeChild(allContent);

        // Log analytics if cookies accepted
        if (cookieAccept?.analytics) {
          logEvent(analytics, analyticsEventName, {
            report_type: reportTitle,
            total_pages: totalPages,
            total_records: data.length,
          });
        }
      })
      .catch((error) => {
        console.error("PDF generation failed:", error);
        document.body.removeChild(allContent);
      });
  };
  return (
    <div id="tableScroll" className="mt-6">
      <div className="container">
        <div className="pb-10">
          <div className="flex justify-between">
            <h2 className="sm:text-lg xl:text-lg text-[15px] text-black font-medium text-start pb-6">
              {title}
            </h2>

            <div className="group relative">
              <button
                onClick={generatePDF}
                className="transition-transform hover:scale-110"
              >
                <img
                  src={DownloadLogo}
                  alt="Download-Icon"
                  className="sm:w-auto sm:h-auto w-[24px] h-[24px]"
                />
              </button>
              <span className="absolute hidden font-lato-regular-400 group-hover:flex justify-center -left-[65px] -top-2 -translate-y-full w-[100px] m-auto px-1 py-1 bg-gray rounded-lg text-center text-white text-[12px] after:content-[''] after:absolute after:left-[80%] after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-[transparent] after:border-b-[transparent] after:border-t-gray">
                {textData.historyData.downoadPDF}
              </span>
            </div>
          </div>

          <div className="border-[1px] border-solid border-[#0752B533] rounded-[8px]">
            <div className="table_container rounded-[8px]">
            <table className="w-full" id="table-to-download">
              <thead>
                <tr className="bg-gray-50">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="py-4 text-start px-4 border-r-[1px] border-b-[1px] border-[#0752B533] text-sm sm:text-[15px] text-black font-medium last:border-r-0"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0 ? "bg-[#F6F8FB]" : "bg-white "
                    } last:border-b-0 border-b-[1px]  border-[#0752B533] text-start `}
                  >
                    {columns.map((column) => {
                      const value = item[column.key];
                      return (
                        <td
                          key={`${index}-${column.key}`}
                          className="py-4 px-4  border-r-[1px] border-[#0752B533] text-sm sm:text-[15px] text-gray-font font-normal text-start last:border-r-0 "
                        >
                          {value === 0 || value === ""
                            ? "-"
                            : column.format(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableLayout;
